import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Mail, Check, MoreVertical, UserPlus, History, LogIn } from 'lucide-react';
import UserManagementModal from './UserManagementModal';
import EmailHistoryModal from './EmailHistoryModal';
import LoginHistoryModal from './LoginHistoryModal';
import { useClient } from '../ClientContext';
import { authApiClient } from '../hooks/useAuthInterceptor';

const styles = {
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '4px',
    width: '185px',
    padding: '4px 16px',
    backgroundColor: '#F3EAFF',
    color: '#381767',
    cursor: 'pointer',
  },
  tableCell: {
    fontWeight: 600,
    minWidth: '150px',
  },
  button: {
    backgroundColor: '#F3EAFF',
    color: '#381767',
  },
  tabs: {
    indicatorColor: {
      backgroundColor: '#772EDD',
      height: '4px',
    },
  },
  tab: {
    backgroundColor: 'transparent',
    '&.Mui-selected': {
      color: 'inherit',
    },
  },
  sentStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#381767',
    backgroundColor: 'transparent',
  },
  deleteOption: {
    color: 'red',
  },
  iconButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  containerBox: {
    backgroundColor: '#FFFFFF',
    padding: '32px',
    borderRadius: '8px',
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
  },
  headerText: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  addButton: {
    backgroundColor: '#772EDD',
    color: '#FFFFFF',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    boxShadow: 'none',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#5C21BF',
    },
  },
  tabsBox: {
    marginBottom: '24px',
    padding: 0,
  },
  tableContainer: {
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 1,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableRowHover: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.07)',
    },
  },
  deliveryStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  statusQueued: {
    backgroundColor: '#FFF8E1',
    color: '#F57F17',
  },
  statusDelivered: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  statusFailed: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
  },
  statusInfo: {
    marginLeft: '4px',
    cursor: 'pointer',
  },
  refreshButton: {
    marginLeft: '8px',
    cursor: 'pointer',
  },
};

const UserManagementTable = ({ accessToken }) => {
  const { selectedClient, config } = useClient();
  const supportsDemoMode = config?.clients?.[selectedClient]?.supportsDemoMode === true;
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadedClient, setLoadedClient] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [invitationStatus, setInvitationStatus] = useState({});
  const [invitationLoading, setInvitationLoading] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [emailHistoryModalOpen, setEmailHistoryModalOpen] = useState(false);
  const [selectedEmailUser, setSelectedEmailUser] = useState(null);
  const [loginHistoryModalOpen, setLoginHistoryModalOpen] = useState(false);
  const [selectedLoginUser, setSelectedLoginUser] = useState(null);
  const fetchRequestIdRef = useRef(0);

  const usersByClientEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users`;
  const adminUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-admin`;
  const AUTH0_PROFILE_BASE_URL = process.env.REACT_APP_AUTH0_PROFILE_BASE_URL;
  const BEATDAPP_DOMAIN = 'beatdapp.com';

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    const requestId = ++fetchRequestIdRef.current;
    setLoadedClient(null);
    setUsers([]);
    setAdminUsers([]);

    try {
      const effectiveClient = selectedClient === 'All Dashboards' ? 'Tidal' : selectedClient;
      const userEndpoint = `${usersByClientEndpoint}?client=${encodeURIComponent(effectiveClient)}`;
      const responses = await Promise.all([
        authApiClient.get(userEndpoint.replace(process.env.REACT_APP_BACKEND_URL, '')),
        authApiClient.get(adminUserEndpoint.replace(process.env.REACT_APP_BACKEND_URL, '')),
      ]);

      const [userData, adminData] = responses.map((res) => res.data);
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      setUsers(userData.users || []);
      setAdminUsers(adminData.adminUsers || []);
      setLoadedClient(selectedClient);
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', err);
    }
  }, [accessToken, adminUserEndpoint, selectedClient, usersByClientEndpoint]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchRequestIdRef.current += 1;
    };
  }, [fetchData]);

  const isBeatdappUser = (user) => user.email.endsWith(`@${BEATDAPP_DOMAIN}`);
  // const formatDate = (dateString) => {
  //   if (!dateString) return '';
  //   const date = new Date(dateString);
  //   const options = {
  //     year: 'numeric',
  //     month: '2-digit',
  //     day: '2-digit',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     hour12: true,
  //   };
  //   return date.toLocaleString('en-US', options);
  // };
  const scopedUsers = loadedClient === selectedClient ? users : [];
  const scopedAdminUsers = loadedClient === selectedClient ? adminUsers : [];
  const clientUsers = scopedUsers.filter((user) => !isBeatdappUser(user));
  const beatdappUsers = scopedUsers.filter(isBeatdappUser);

  const displayedUsers =
    selectedTab === 0
      ? [...scopedUsers, ...scopedAdminUsers]
      : selectedTab === 1
        ? clientUsers
        : selectedTab === 2
          ? beatdappUsers
          : scopedAdminUsers;

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const onButtonClick = async (user) => {
    let userType = selectedClient;
    try {
      setInvitationLoading((prev) => ({ ...prev, [user.email]: true }));
      await authApiClient.post('/admin/send-invitation', {
        email: user.email,
        userType: userType,
        id: user.id,
      });

      setInvitationStatus((prevStatus) => ({
        ...prevStatus,
        [user.email]: 'sent',
      }));
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(error.message);
    } finally {
      setInvitationLoading((prev) => ({ ...prev, [user.email]: false }));
    }
  };

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const deleteUser = async (userId) => {
    try {
      await authApiClient.delete(`/admin/user/${userId}`);

      alert('User deleted successfully');
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
      setAdminUsers((prevAdminUsers) => prevAdminUsers.filter((admin) => admin.id !== userId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleOptionClick = (option) => {
    handleMenuClose();

    if (selectedUser) {
      if (option === 'Update User') {
        openModal('Update User', selectedUser);
      } else if (option === 'Delete User') {
        const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedUser.name}?`);
        if (confirmDelete) {
          deleteUser(selectedUser.id);
        }
      } else if (option === 'Go to Auth0 Profile') {
        if (selectedUser.auth0_id) {
          const auth0ProfileUrl = `${AUTH0_PROFILE_BASE_URL}${selectedUser.auth0_id}`;
          window.open(auth0ProfileUrl, '_blank');
        } else {
          alert('Auth0 ID not available for this user.');
        }
      }
    }
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const openEmailHistoryModal = (user) => {
    setSelectedEmailUser(user);
    setEmailHistoryModalOpen(true);
  };

  const closeEmailHistoryModal = () => {
    setEmailHistoryModalOpen(false);
    setSelectedEmailUser(null);
  };

  const openLoginHistoryModal = (user) => {
    setSelectedLoginUser(user);
    setLoginHistoryModalOpen(true);
  };

  const closeLoginHistoryModal = () => {
    setLoginHistoryModalOpen(false);
    setSelectedLoginUser(null);
  };

  const renderDeliveryStatus = (user) => {
    return (
      <Tooltip title="View Email History">
        <IconButton size="small" onClick={() => openEmailHistoryModal(user)}>
          <History size={16} />
        </IconButton>
      </Tooltip>
    );
  };

  const renderLoginHistoryAction = (user) => {
    return (
      <Tooltip title="View Login History">
        <IconButton size="small" onClick={() => openLoginHistoryModal(user)}>
          <LogIn size={16} />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box sx={styles.containerBox}>
      <Box sx={styles.headerBox}>
        <Box>
          <Typography sx={styles.headerText}>Users</Typography>
          <Typography>A list of all users that have access to {selectedClient}</Typography>
        </Box>
        <Button variant="contained" onClick={() => openModal('Add New User')} sx={styles.addButton}>
          <UserPlus size={18} style={{ marginRight: '8px' }} />
          Add New User
        </Button>
      </Box>
      <Box sx={styles.tabsBox}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            sx: styles.tabs.indicatorColor,
          }}
          textColor="primary"
        >
          <Tab disableRipple sx={styles.tab} label="All Users" />
          <Tab disableRipple sx={styles.tab} label={`${selectedClient} Users`} />
          <Tab disableRipple sx={styles.tab} label="Beatdapp Users" />
          <Tab disableRipple sx={styles.tab} label="Admin Users" />
        </Tabs>
      </Box>
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableCell}>Name</TableCell>
              <TableCell sx={styles.tableCell}>Email</TableCell>
              <TableCell sx={styles.tableCell}>User Role</TableCell>
              <TableCell sx={styles.tableCell}>Auth0Id</TableCell>
              <TableCell sx={styles.tableCell}>Internal</TableCell>
              {supportsDemoMode && <TableCell sx={styles.tableCell}>Demo Mode</TableCell>}
              <TableCell sx={styles.tableCell}>Invite</TableCell>
              <TableCell sx={styles.tableCell}>Email History</TableCell>
              <TableCell sx={styles.tableCell}>Login History</TableCell>
              <TableCell sx={styles.tableCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedUsers.map((user, index) => (
              <TableRow key={index} sx={styles.tableRowHover}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.auth0_role_id}</TableCell>
                <TableCell>{user.auth0_id}</TableCell>
                <TableCell>{user.internal ? <Check size={16} /> : ''}</TableCell>
                {supportsDemoMode && <TableCell>{user.demo_mode ? <Check size={16} /> : ''}</TableCell>}
                <TableCell>
                  <Box
                    sx={{
                      ...(invitationStatus[user.email] === 'sent' ? styles.sentStatus : styles.buttonBox),
                    }}
                  >
                    {invitationStatus[user.email] === 'sent' ? (
                      <>
                        <Check size={16} />
                        <Typography>Invitation Sent</Typography>
                      </>
                    ) : invitationLoading[user.email] ? (
                      <>
                        <CircularProgress color="inherit" size={16} />
                        <Typography>Sending...</Typography>
                      </>
                    ) : (
                      <>
                        <Mail />
                        <Button sx={styles.button} onClick={() => onButtonClick(user)}>
                          Send Invitation
                        </Button>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{renderDeliveryStatus(user)}</TableCell>
                <TableCell>{renderLoginHistoryAction(user)}</TableCell>
                <TableCell>
                  <IconButton sx={styles.iconButton} onClick={(event) => handleMenuClick(event, user)}>
                    <MoreVertical />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleOptionClick('Go to Auth0 Profile')}>Go to Auth0 Profile</MenuItem>
        <MenuItem onClick={() => handleOptionClick('Update User')}>Update User</MenuItem>
        <MenuItem onClick={() => handleOptionClick('Delete User')} sx={styles.deleteOption}>
          Delete User
        </MenuItem>
      </Menu>
      <UserManagementModal
        modalType={modalType}
        open={modalOpen}
        onClose={closeModal}
        onSuccess={fetchData}
        selectedUser={selectedUser}
        accessToken={accessToken}
        accountTypeUsers={clientUsers}
      />
      <EmailHistoryModal
        open={emailHistoryModalOpen}
        onClose={closeEmailHistoryModal}
        user={selectedEmailUser}
        accessToken={accessToken}
      />
      <LoginHistoryModal
        open={loginHistoryModalOpen}
        onClose={closeLoginHistoryModal}
        user={selectedLoginUser}
        accessToken={accessToken}
      />
    </Box>
  );
};

export default UserManagementTable;
